import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import CustomerLayout from "../../layouts/CustomerLayout";
import Config from "../../utils/Config";
import {
  Calendar,
  Clock,
  Edit2,
  Eye,
  Trash2,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Send,
  AlertCircle,
  MessageSquare,
  ChevronDown,
  LayoutGrid,
  List,
  Plus,
  User,
  Hash,
  Users,
  Lock,
} from "lucide-react";

const BASE_URL = Config.baseUrl;

// ─── Auth helper ──────────────────────────────────────────────────────────────
const getAuthHeaders = () => {
  const token =
    localStorage.getItem("txtconnect_ng_user_token") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("txtconnect_token") ||
    "";
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, "0");

function formatDisplay(isoString) {
  if (!isoString) return "—";
  const d = new Date(isoString);
  return d.toLocaleDateString("en-GB", {
    weekday: "short", day: "numeric", month: "short",
    year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function isoToPayload(isoString) {
  if (!isoString) return "";
  const d = new Date(isoString);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
}

// Snaps an arbitrary minute value to the nearest 15-minute slot (00/15/30/45).
function snapMinute(m) {
  const snapped = Math.round(m / 15) * 15;
  return pad(snapped % 60);
}

function parseScheduledAt(str) {
  if (!str) return { date: null, hour: "09", minute: "00", ampm: "AM" };
  const d = new Date(str.replace(" ", "T") + "Z");
  let h = d.getUTCHours();
  const ampm = h >= 12 ? "PM" : "AM";
  if (h > 12) h -= 12;
  if (h === 0) h = 12;
  return { date: d, hour: pad(h), minute: snapMinute(d.getUTCMinutes()), ampm };
}

function buildScheduledAt(date, hour, minute, ampm) {
  if (!date) return "";
  let h = parseInt(hour, 10);
  if (ampm === "PM" && h !== 12) h += 12;
  if (ampm === "AM" && h === 12) h = 0;
  const d = new Date(date);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(h)}:${pad(minute)}:00`;
}

const STATUS_COLORS = {
  scheduled: "bg-blue-50 text-blue-700 border-blue-200",
  sent:      "bg-green-50 text-green-700 border-green-200",
  failed:    "bg-red-50 text-red-700 border-red-200",
  cancelled: "bg-gray-50 text-gray-600 border-gray-200",
};

// ─── Mini Calendar ────────────────────────────────────────────────────────────
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const WDAYS  = ["Su","Mo","Tu","We","Th","Fr","Sa"];

const MiniCalendar = ({ selectedDate, onChange, minDate }) => {
  const today = new Date();
  const [vYear,  setVYear]  = useState(selectedDate ? new Date(selectedDate).getFullYear() : today.getFullYear());
  const [vMonth, setVMonth] = useState(selectedDate ? new Date(selectedDate).getMonth()    : today.getMonth());

  const firstDay    = new Date(vYear, vMonth, 1).getDay();
  const daysInMonth = new Date(vYear, vMonth + 1, 0).getDate();
  const cells = Array(firstDay).fill(null).concat(Array.from({ length: daysInMonth }, (_, i) => i + 1));

  const prev = () => vMonth === 0 ? (setVMonth(11), setVYear(y => y-1)) : setVMonth(m => m-1);
  const next = () => vMonth === 11 ? (setVMonth(0),  setVYear(y => y+1)) : setVMonth(m => m+1);

  const disabled = (day) => {
    if (!day || !minDate) return !day;
    const d = new Date(vYear, vMonth, day); d.setHours(0,0,0,0);
    const m = new Date(minDate);            m.setHours(0,0,0,0);
    return d < m;
  };
  const selected = (day) => {
    if (!day || !selectedDate) return false;
    const s = new Date(selectedDate);
    return s.getFullYear()===vYear && s.getMonth()===vMonth && s.getDate()===day;
  };
  const isToday = (day) => day && today.getFullYear()===vYear && today.getMonth()===vMonth && today.getDate()===day;

  return (
    <div className="select-none">
      <div className="flex items-center justify-between mb-3">
        <button onClick={prev} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600 transition-colors"><ChevronLeft className="w-4 h-4"/></button>
        <span className="text-sm font-semibold text-gray-800">{MONTHS[vMonth]} {vYear}</span>
        <button onClick={next} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600 transition-colors"><ChevronRight className="w-4 h-4"/></button>
      </div>
      <div className="grid grid-cols-7 mb-1">
        {WDAYS.map(d => <div key={d} className="text-center text-[10px] font-semibold text-gray-400 py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((day, i) => (
          <button key={i} disabled={!day || disabled(day)}
            onClick={() => day && !disabled(day) && onChange(new Date(vYear, vMonth, day))}
            className={`aspect-square flex items-center justify-center rounded-lg text-xs font-medium transition-all
              ${!day ? "invisible pointer-events-none" : ""}
              ${disabled(day) ? "text-gray-300 cursor-not-allowed" : "cursor-pointer"}
              ${selected(day) ? "bg-red-600 text-white shadow-sm" : isToday(day) && !disabled(day) ? "bg-red-50 text-red-600 border border-red-200" : !disabled(day) ? "text-gray-700 hover:bg-red-50 hover:text-red-600" : ""}
            `}>{day}</button>
        ))}
      </div>
    </div>
  );
};

// ─── Scroll Drum ──────────────────────────────────────────────────────────────
const ITEM_H = 36;

const Drum = ({ items, value, onChange }) => {
  const ref = useRef(null);
  const locking = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const idx = items.indexOf(value);
    if (idx >= 0) el.scrollTop = idx * ITEM_H;
  }, [value, items]);

  const handleScroll = (e) => {
    if (locking.current) return;
    const idx = Math.max(0, Math.min(Math.round(e.target.scrollTop / ITEM_H), items.length - 1));
    if (items[idx] !== value) onChange(items[idx]);
  };

  const handleClick = (item, idx) => {
    onChange(item);
    if (ref.current) {
      locking.current = true;
      ref.current.scrollTo({ top: idx * ITEM_H, behavior: "smooth" });
      setTimeout(() => { locking.current = false; }, 300);
    }
  };

  return (
    <div className="relative h-[108px] overflow-hidden rounded-xl border border-gray-100 bg-white">
      <div className="pointer-events-none absolute top-0 inset-x-0 h-8 z-10 bg-gradient-to-b from-white to-transparent"/>
      <div className="pointer-events-none absolute bottom-0 inset-x-0 h-8 z-10 bg-gradient-to-t from-white to-transparent"/>
      <div className="pointer-events-none absolute top-[36px] left-1 right-1 h-9 z-0 rounded-lg border-y border-red-100 bg-red-50/60"/>
      <div ref={ref} onScroll={handleScroll}
        className="h-full overflow-y-scroll scrollbar-hide"
        style={{ scrollSnapType: "y mandatory", WebkitOverflowScrolling: "touch" }}>
        <div style={{ height: ITEM_H }}/>
        {items.map((item, idx) => (
          <div key={item} onClick={() => handleClick(item, idx)}
            style={{ height: ITEM_H, scrollSnapAlign: "start" }}
            className={`flex items-center justify-center cursor-pointer text-sm font-medium transition-colors select-none ${value === item ? "text-red-600 font-bold" : "text-gray-400 hover:text-gray-600"}`}>
            {item}
          </div>
        ))}
        <div style={{ height: ITEM_H }}/>
      </div>
    </div>
  );
};

const HOURS = Array.from({ length: 12 }, (_, i) => pad(i + 1));
const MINUTES = ["00", "15", "30", "45"];

const TimePicker = ({ hour, minute, ampm, onHourChange, onMinuteChange, onAmpmChange }) => (
  <div className="flex items-center gap-3">
    <div className="flex-1">
      <div className="text-[10px] text-center text-gray-400 mb-1 font-medium uppercase tracking-wide">Hour</div>
      <Drum items={HOURS} value={hour} onChange={onHourChange} />
    </div>
    <div className="flex-1">
      <div className="text-[10px] text-center text-gray-400 mb-1 font-medium uppercase tracking-wide">Min</div>
      <Drum items={MINUTES} value={minute} onChange={onMinuteChange} />
    </div>
    <div className="flex flex-col gap-1.5 flex-shrink-0">
      <div className="text-[10px] text-center text-gray-400 mb-1 font-medium uppercase tracking-wide invisible">·</div>
      {["AM","PM"].map(p => (
        <button key={p} onClick={() => onAmpmChange(p)}
          className={`px-3 py-2 rounded-xl text-xs font-bold transition-all ${ampm===p ? "bg-red-600 text-white shadow-sm shadow-red-200" : "bg-gray-100 text-gray-500 hover:bg-red-50 hover:text-red-600"}`}>
          {p}
        </button>
      ))}
    </div>
  </div>
);

// ─── View / Edit Modal ────────────────────────────────────────────────────────
const CampaignModal = ({ campaign, mode: initialMode, onClose, onSaved }) => {
  const [mode, setMode] = useState(initialMode);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const parsed = parseScheduledAt(campaign.scheduled_at ? isoToPayload(campaign.scheduled_at) : "");
  const [campaignName, setCampaignName] = useState(campaign.name || "");
  const [content,      setContent]      = useState(campaign.snd_txt || "");
  const [selectedDate, setSelectedDate] = useState(parsed.date);
  const [hour,  setHour]                = useState(parsed.hour);
  const [minute, setMinute]             = useState(parsed.minute);
  const [ampm,  setAmpm]                = useState(parsed.ampm);

  const isValid = campaignName.trim() && content.trim() && selectedDate;
  const displayScheduled = campaign.scheduled_at ? formatDisplay(campaign.scheduled_at) : "—";
  const isSent = campaign.status === "sent";
  const msgLen = (campaign.snd_txt || "").length;

  const handleSave = async () => {
    if (!isValid) return;
    setIsSubmitting(true);
    try {
      const payload = {
        name:         campaignName.trim(),
        scheduled_at: buildScheduledAt(selectedDate, hour, minute, ampm),
        status:       "scheduled",
        content:      content.trim(),
      };
      const res = await axios.put(
        `${BASE_URL}api/user/update/campaign/${campaign.id}`,
        payload,
        { headers: getAuthHeaders() }
      );
      const d = res.data?.data;
      if (d?.status_code === "000" || d?.status_code === "200" || res.status === 200 || res.status === 201) {
        toast.success("Campaign updated successfully!");
        onSaved();
        onClose();
      } else {
        toast.error(d?.reason || d?.message || "Update failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.data?.reason || err?.response?.data?.message || "An error occurred.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm bg-black/40"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.94, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.94, y: 24 }}
        transition={{ type: "spring", stiffness: 320, damping: 28 }}
        className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-red-50 to-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-xl border border-red-200">
              {mode === "edit" ? <Edit2 className="w-4 h-4 text-red-600"/> : <Eye className="w-4 h-4 text-red-600"/>}
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-800">{mode === "edit" ? "Edit Campaign" : "View Campaign"}</h2>
              <p className="text-xs text-gray-500">{campaign.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {mode === "view" && !isSent && (
              <button onClick={() => setMode("edit")}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors">
                <Edit2 className="w-3 h-3"/> Edit
              </button>
            )}
            <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
              <X className="w-4 h-4"/>
            </button>
          </div>
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[75vh] overflow-y-auto">
          <div className="flex items-center gap-2">
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${STATUS_COLORS[campaign.status] || STATUS_COLORS.scheduled}`}>
              {campaign.status}
            </span>
            <span className="text-xs text-gray-400">ID: {campaign.id}</span>
          </div>

          {mode === "view" ? (
            <div className="space-y-4">
              {isSent && (
                <div className="flex items-start gap-2 p-3 rounded-xl bg-gray-50 border border-gray-100">
                  <Lock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5"/>
                  <p className="text-xs text-gray-500">This campaign has already been sent, so it can no longer be edited or deleted.</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <InfoTile icon={User}          label="Sender ID"      value={campaign.snd_sender} />
                <InfoTile icon={MessageSquare} label="SMS Type"       value={campaign.sms_type} />
                <InfoTile icon={Calendar}      label="Scheduled At"   value={displayScheduled} />
                <InfoTile icon={Hash}          label="Required Units" value={campaign.required_units} />
              </div>

              <div>
                <div className="flex items-center gap-1.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  <MessageSquare className="w-3 h-3 text-red-500"/> Message Content
                </div>
                <div className="p-3.5 bg-gradient-to-br from-red-50/50 to-white rounded-xl border border-red-100">
                  <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{campaign.snd_txt}</p>
                  <div className="flex justify-end mt-2 pt-2 border-t border-red-100/70">
                    <span className="text-[10px] font-medium text-gray-400">{msgLen} chars · {Math.ceil(msgLen / 160) || 1} SMS</span>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-1.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                    <Users className="w-3 h-3 text-red-500"/> Contact Groups
                  </div>
                  {(campaign.contact_group_slugs || []).length > 0 && (
                    <span className="text-[10px] font-semibold text-gray-400">
                      {campaign.contact_group_slugs.length} group{campaign.contact_group_slugs.length !== 1 ? "s" : ""}
                    </span>
                  )}
                </div>
                {(campaign.contact_group_slugs || []).length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {campaign.contact_group_slugs.map((slug) => (
                      <span key={slug} className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-100 rounded-lg text-xs font-mono">{slug}</span>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic">No contact groups attached.</p>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Campaign Name <span className="text-red-500">*</span></label>
                <input type="text" value={campaignName} onChange={e => setCampaignName(e.target.value)} maxLength={80}
                  className="w-full px-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"/>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Message Content <span className="text-red-500">*</span></label>
                <textarea value={content} onChange={e => setContent(e.target.value)} rows={4}
                  className="w-full px-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all resize-none"/>
                <div className="text-right text-[10px] text-gray-400 mt-1">{content.length} chars · {Math.ceil(content.length/160) || 1} SMS</div>
              </div>
              <div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                  <Calendar className="w-3.5 h-3.5 text-red-500"/> Date <span className="text-red-500">*</span>
                </label>
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <MiniCalendar selectedDate={selectedDate} onChange={setSelectedDate} minDate={new Date()} />
                </div>
              </div>
              <div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                  <Clock className="w-3.5 h-3.5 text-red-500"/> Time <span className="text-red-500">*</span>
                </label>
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <TimePicker hour={hour} minute={minute} ampm={ampm} onHourChange={setHour} onMinuteChange={setMinute} onAmpmChange={setAmpm}/>
                </div>
                <p className="mt-2 text-xs text-center text-gray-500">
                  Selected: <span className="font-semibold text-gray-700">{hour}:{minute} {ampm}</span>
                </p>
              </div>
              {!isValid && (
                <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5"/>
                  <p className="text-xs text-amber-700">Fill in all required fields to save.</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-3 bg-gray-50/50">
          <button onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors">
            {mode === "view" ? "Close" : "Cancel"}
          </button>
          {mode === "edit" && (
            <motion.button onClick={handleSave} disabled={!isValid || isSubmitting}
              whileHover={isValid && !isSubmitting ? { scale: 1.02 } : {}}
              whileTap={isValid && !isSubmitting ? { scale: 0.97 } : {}}
              className={`flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-xl transition-all shadow-sm ${isValid && !isSubmitting ? "bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 shadow-red-200" : "bg-gray-200 text-gray-400 cursor-not-allowed"}`}>
              {isSubmitting
                ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/> Saving...</>
                : <><Send className="w-4 h-4"/> Save Changes</>}
            </motion.button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

const InfoTile = ({ icon: Icon, label, value }) => (
  <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
    <div className="flex items-center gap-1.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
      <Icon className="w-3 h-3 text-red-500"/> {label}
    </div>
    <div className="text-sm text-gray-800 font-semibold truncate">{value ?? "—"}</div>
  </div>
);

// comment

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────
const DeleteModal = ({ campaign, onClose, onDeleted }) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      // TODO: placeholder endpoint — swap for the real delete endpoint once provided
      const res = await axios.delete(`${BASE_URL}api/user/delete/campaign/${campaign.id}`, { headers: getAuthHeaders() });
      const d = res.data?.data;
      if (d?.status_code === "000" || d?.status_code === "200" || res.status === 200 || res.status === 204) {
        toast.success("Campaign deleted.");
        onDeleted();
        onClose();
      } else {
        toast.error(d?.reason || "Delete failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.data?.reason || "An error occurred.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm bg-black/40"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.94, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.94, y: 16 }}
        transition={{ type: "spring", stiffness: 320, damping: 28 }}
        className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden"
      >
        <div className="p-6 text-center">
          <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-100">
            <Trash2 className="w-6 h-6 text-red-600"/>
          </div>
          <h3 className="text-lg font-bold text-gray-800 mb-1">Delete Campaign?</h3>
          <p className="text-sm text-gray-500 mb-1"><span className="font-semibold text-gray-700">"{campaign.name}"</span></p>
          <p className="text-xs text-gray-400 mb-6">This action cannot be undone.</p>
          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">Cancel</button>
            <button onClick={handleDelete} disabled={isDeleting}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-red-500 to-red-600 rounded-xl hover:from-red-600 hover:to-red-700 transition-all disabled:opacity-60">
              {isDeleting
                ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/> Deleting...</>
                : <><Trash2 className="w-4 h-4"/> Delete</>}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
//comment
// ─── Campaign Card (Grid View) ────────────────────────────────────────────────
const CampaignCard = ({ campaign, onView, onEdit, onDelete }) => {
  const isSent = campaign.status === "sent";
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:border-red-100 transition-all"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-gray-800 truncate text-sm mb-0.5">{campaign.name}</h4>
          <p className="text-xs text-gray-500 truncate">{campaign.snd_txt}</p>
        </div>
        <span className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold border ${STATUS_COLORS[campaign.status] || STATUS_COLORS.scheduled}`}>
          {campaign.status}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
        <div className="flex items-center gap-1.5 text-gray-600">
          <MessageSquare className="w-3.5 h-3.5 text-red-400 flex-shrink-0"/>
          <span className="truncate">{campaign.snd_sender}</span>
        </div>
        <div className="flex items-center gap-1.5 text-gray-600">
          <Calendar className="w-3.5 h-3.5 text-red-400 flex-shrink-0"/>
          <span className="truncate">{formatDisplay(campaign.scheduled_at)}</span>
        </div>
      </div>
      <div className="flex items-center gap-2 pt-2 border-t border-gray-50">
        <button onClick={() => onView(campaign)}
          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors border border-blue-100">
          <Eye className="w-3.5 h-3.5"/> View
        </button>
        {!isSent && (
          <button onClick={() => onEdit(campaign)}
            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-amber-600 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors border border-amber-100">
            <Edit2 className="w-3.5 h-3.5"/> Edit
          </button>
        )}
        {!isSent && (
          <button onClick={() => onDelete(campaign)}
            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors border border-red-100">
            <Trash2 className="w-3.5 h-3.5"/> Delete
          </button>
        )}
      </div>
    </motion.div>
  );
};

// ─── Campaign Table Row (Table View) ─────────────────────────────────────────
const CampaignTableRow = ({ campaign, index, onView, onEdit, onDelete }) => {
  const isSent = campaign.status === "sent";
  return (
    <motion.tr
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03 }}
      className="border-b border-gray-50 hover:bg-red-50/30 transition-colors group"
    >
      <td className="px-4 py-3.5">
        <div className="font-semibold text-sm text-gray-800 truncate max-w-[160px]">{campaign.name}</div>
      </td>
      <td className="px-4 py-3.5">
        <div className="text-xs text-gray-500 truncate max-w-[200px]">{campaign.snd_txt}</div>
      </td>
      <td className="px-4 py-3.5">
        <div className="text-xs text-gray-700 font-medium">{campaign.snd_sender || "—"}</div>
      </td>
      <td className="px-4 py-3.5">
        <div className="text-xs text-gray-600 whitespace-nowrap">{formatDisplay(campaign.scheduled_at)}</div>
      </td>
      <td className="px-4 py-3.5">
        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${STATUS_COLORS[campaign.status] || STATUS_COLORS.scheduled}`}>
          {campaign.status}
        </span>
      </td>
      <td className="px-4 py-3.5">
        <div className="flex items-center gap-2 opacity-60 group-hover:opacity-100 transition-opacity">
          <button onClick={() => onView(campaign)}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors border border-blue-100">
            <Eye className="w-3 h-3"/> View
          </button>
          {!isSent && (
            <button onClick={() => onEdit(campaign)}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-amber-600 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors border border-amber-100">
              <Edit2 className="w-3 h-3"/> Edit
            </button>
          )}
          {!isSent && (
            <button onClick={() => onDelete(campaign)}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors border border-red-100">
              <Trash2 className="w-3 h-3"/> Delete
            </button>
          )}
        </div>
      </td>
    </motion.tr>
  );
};

// ─── Pagination Controls ──────────────────────────────────────────────────────
const PaginationControls = ({ pagination, onPageChange, onPerPageChange }) => {
  const { currentPage, lastPage, total, from, to } = pagination;
  if (total === 0) return null;

  const pages = Array.from({ length: Math.min(lastPage, 7) }, (_, i) => {
    if (lastPage <= 7)               return i + 1;
    if (currentPage <= 4)            return i + 1;
    if (currentPage >= lastPage - 3) return lastPage - 6 + i;
    return currentPage - 3 + i;
  });

  return (
    <div className="flex flex-col gap-4 mt-6 sm:flex-row sm:justify-between sm:items-center">
      <div className="text-sm text-gray-600">
        Showing <span className="font-semibold">{from}</span> to <span className="font-semibold">{to}</span> of{" "}
        <span className="font-semibold">{total}</span> campaign{total !== 1 ? "s" : ""}
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${currentPage === 1 ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400"}`}>
          <ChevronLeft className="w-4 h-4"/> Previous
        </button>
        <div className="flex gap-1">
          {pages.map(p => (
            <button key={p} onClick={() => onPageChange(p)}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-all ${currentPage === p ? "bg-red-600 text-white" : "bg-white text-gray-700 border border-gray-300 hover:bg-red-50 hover:border-red-300"}`}>
              {p}
            </button>
          ))}
        </div>
        <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === lastPage}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${currentPage === lastPage ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400"}`}>
          Next <ChevronRight className="w-4 h-4"/>
        </button>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const ScheduledMessages = () => {
  const navigate = useNavigate();

  const [campaigns,       setCampaigns]       = useState([]);
  const [isLoading,       setIsLoading]       = useState(true);
  const [searchTerm,      setSearchTerm]      = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateFrom,        setDateFrom]        = useState("");
  const [dateTo,          setDateTo]          = useState("");
  const [statusFilter,    setStatusFilter]    = useState("");
  const [viewMode,        setViewMode]        = useState("table"); // "card" | "table"

  const [pagination, setPagination] = useState({
    currentPage: 1, perPage: 15,
    total: 0, lastPage: 1, from: 0, to: 0,
  });

  const [viewModal,   setViewModal]   = useState(null);
  const [editModal,   setEditModal]   = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPagination(p => ({ ...p, currentPage: 1 }));
    }, 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const fetchCampaigns = useCallback(async (page, perPage, search, status, from, to) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: perPage });
      if (search) params.set("search", search);
      if (status) params.set("status", status);
      if (from)   params.set("date_from", from);
      if (to)     params.set("date_to", to);

      const res = await axios.get(`${BASE_URL}api/user/campaigns?${params}`, { headers: getAuthHeaders() });
      const d = res.data?.data?.data;
      if (d) {
        setCampaigns(d.campaigns || []);
        const pg = d.pagination || {};
        setPagination({
          currentPage: pg.current_page || page,
          perPage:     pg.per_page     || perPage,
          total:       pg.total        || 0,
          lastPage:    pg.last_page    || 1,
          from:        pg.from         || 0,
          to:          pg.to           || 0,
        });
      }
    } catch (err) {
      toast.error("Failed to load campaigns.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns(pagination.currentPage, pagination.perPage, debouncedSearch, statusFilter, dateFrom, dateTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.perPage, debouncedSearch, statusFilter, dateFrom, dateTo]);

  const handlePageChange    = (p)  => setPagination(prev => ({ ...prev, currentPage: p }));
  const handlePerPageChange = (pp) => setPagination(prev => ({ ...prev, perPage: pp, currentPage: 1 }));
  const handleStatusChange  = (s)  => { setStatusFilter(s); setPagination(p => ({ ...p, currentPage: 1 })); };
  const refresh = () => fetchCampaigns(pagination.currentPage, pagination.perPage, debouncedSearch, statusFilter, dateFrom, dateTo);

  // ── Skeletons ──
  const CardSkeleton = () => (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="p-4 bg-white rounded-xl border border-gray-100 animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"/>
          <div className="h-3 bg-gray-100 rounded w-full mb-3"/>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="h-3 bg-gray-100 rounded"/>
            <div className="h-3 bg-gray-100 rounded"/>
          </div>
          <div className="flex gap-2 pt-2 border-t border-gray-50">
            <div className="flex-1 h-7 bg-gray-100 rounded-lg"/>
            <div className="flex-1 h-7 bg-gray-100 rounded-lg"/>
          </div>
        </div>
      ))}
    </div>
  );

  const TableSkeleton = () => (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      <div className="h-11 bg-gray-50 border-b border-gray-100"/>
      {[...Array(7)].map((_, i) => (
        <div key={i} className="flex gap-4 px-4 py-3.5 border-b border-gray-50 animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-32"/>
          <div className="h-4 bg-gray-100 rounded flex-1"/>
          <div className="h-4 bg-gray-100 rounded w-20"/>
          <div className="h-4 bg-gray-100 rounded w-32"/>
          <div className="h-5 bg-gray-100 rounded-full w-20"/>
          <div className="h-6 bg-gray-100 rounded w-28"/>
        </div>
      ))}
    </div>
  );

  return (
    <CustomerLayout>
      <div className="px-4 pt-20 min-h-screen bg-white lg:pt-20 md:p-6">
        <div className="mx-auto max-w-6xl">

          {/* ── Page Header ── */}
          <div className="relative p-4 mb-6 bg-white border border-red-100 rounded-2xl shadow-sm md:p-6">
            <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-red-50/50 blur-xl -z-10"/>
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-xl font-bold text-gray-800 sm:text-2xl md:text-3xl">Scheduled Campaigns</h1>
                <p className="text-sm text-gray-600 mt-1">Manage your scheduled bulk SMS campaigns</p>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                {/* Campaign count */}
                <div className="inline-flex items-center gap-2 px-3 py-2 bg-red-50 rounded-lg border border-red-100">
                  <Calendar className="w-4 h-4 text-red-600"/>
                  <span className="text-sm font-medium text-red-700">
                    {pagination.total} Campaign{pagination.total !== 1 ? "s" : ""}
                  </span>
                </div>

                {/* Card / Table toggle */}
                <div className="flex items-center bg-gray-100 rounded-lg p-0.5 border border-gray-200">
                  <button
                    onClick={() => setViewMode("card")}
                    title="Card view"
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                      viewMode === "card"
                        ? "bg-white text-red-600 shadow-sm border border-red-100"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    <LayoutGrid className="w-3.5 h-3.5"/>
                    <span className="hidden sm:inline">Cards</span>
                  </button>
                  <button
                    onClick={() => setViewMode("table")}
                    title="Table view"
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                      viewMode === "table"
                        ? "bg-white text-red-600 shadow-sm border border-red-100"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    <List className="w-3.5 h-3.5"/>
                    <span className="hidden sm:inline">Table</span>
                  </button>
                </div>

                {/* Refresh */}
                <button onClick={refresh}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors border border-gray-200">
                  <RefreshCw className="w-3.5 h-3.5"/>
                  <span className="hidden sm:inline">Refresh</span>
                </button>

                {/* New Campaign */}
                <motion.button
                  onClick={() => navigate("/bulk-sms")}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white bg-gradient-to-r from-red-500 to-red-600 rounded-lg hover:from-red-600 hover:to-red-700 shadow-sm shadow-red-200 transition-all md:text-sm"
                >
                  <Plus className="w-4 h-4"/>
                  <span>New Campaign</span>
                </motion.button>
              </div>
            </div>
          </div>

          {/* ── Filters ── */}
          <div className="p-4 mb-6 bg-white rounded-2xl border border-red-100 shadow-sm">
            <div className="flex flex-wrap gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
                <input type="text" placeholder="Search campaigns..." value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"/>
                {searchTerm && (
                  <button onClick={() => setSearchTerm("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <X className="w-3.5 h-3.5"/>
                  </button>
                )}
              </div>
              <div className="relative">
                <select value={statusFilter} onChange={e => handleStatusChange(e.target.value)}
                  className="pl-3 pr-8 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 appearance-none cursor-pointer">
                  <option value="">All Statuses</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="sent">Sent</option>
                  <option value="failed">Failed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none"/>
              </div>
            </div>
          </div>

          {/* ── Content ── */}
          <AnimatePresence mode="wait">
            {isLoading ? (
              <motion.div key="skeleton" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                {viewMode === "card" ? <CardSkeleton /> : <TableSkeleton />}
              </motion.div>

            ) : campaigns.length === 0 ? (
              <motion.div
                key="empty"
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="py-20 text-center bg-white rounded-2xl border border-gray-100"
              >
                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-100">
                  <Calendar className="w-8 h-8 text-red-300"/>
                </div>
                <h3 className="text-lg font-bold text-gray-700 mb-1">No Campaigns Found</h3>
                <p className="text-sm text-gray-500 mb-5">
                  {searchTerm || dateFrom || dateTo || statusFilter
                    ? "Try adjusting your filters."
                    : "You haven't scheduled any campaigns yet."}
                </p>
                {!searchTerm && !statusFilter && (
                  <motion.button
                    onClick={() => navigate("/bulk-messages")}
                    whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-red-500 to-red-600 rounded-xl hover:from-red-600 hover:to-red-700 shadow-sm shadow-red-200 transition-all"
                  >
                    <Plus className="w-4 h-4"/> Create your first campaign
                  </motion.button>
                )}
              </motion.div>

            ) : viewMode === "card" ? (
              <motion.div key="cards" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {campaigns.map(c => (
                    <CampaignCard key={c.id} campaign={c}
                      onView={camp => setViewModal(camp)}
                      onEdit={camp => setEditModal(camp)}
                      onDelete={camp => setDeleteModal(camp)}
                    />
                  ))}
                </div>
                <PaginationControls pagination={pagination} onPageChange={handlePageChange} onPerPageChange={handlePerPageChange}/>
              </motion.div>

            ) : (
              <motion.div key="table" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[700px]">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-100">
                          {["Name","Message","Sender","Scheduled","Status","Actions"].map(h => (
                            <th key={h} className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {campaigns.map((c, i) => (
                          <CampaignTableRow key={c.id} campaign={c} index={i}
                            onView={camp => setViewModal(camp)}
                            onEdit={camp => setEditModal(camp)}
                            onDelete={camp => setDeleteModal(camp)}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <PaginationControls pagination={pagination} onPageChange={handlePageChange} onPerPageChange={handlePerPageChange}/>
              </motion.div>
            )}
          </AnimatePresence>

        </div>
      </div>

      {/* ── Modals ── */}
      <AnimatePresence>
        {viewModal && (
          <CampaignModal key="view" campaign={viewModal} mode="view"
            onClose={() => setViewModal(null)} onSaved={refresh}/>
        )}
        {editModal && (
          <CampaignModal key="edit" campaign={editModal} mode="edit"
            onClose={() => setEditModal(null)} onSaved={refresh}/>
        )}
        {deleteModal && (
          <DeleteModal campaign={deleteModal}
            onClose={() => setDeleteModal(null)} onDeleted={refresh}/>
        )}
      </AnimatePresence>
    </CustomerLayout>
  );
};

export default ScheduledMessages;